import moment from "moment";
import { Label } from "semantic-ui-react";

import { Checkbox } from "@/components/ui/checkbox";
import { getPercentage } from "components/helpers";
import { campaignConstants, statusColors } from "constants/Campaign";
import { CampaignsTableActions } from "./CampaignsTableActions";
import { getUsersForFiltersQuery } from "helpers/user";
import { SELECT_COLUMN_ID } from "constants/Constants";
import { TableLink } from "components/BaseTableV3/TableLink";

const getCampaignStatusDisplay = status => {
  if (status && Object.keys(statusColors).includes(status.toString())) {
    return statusColors[status];
  } else {
    return { color: "grey", content: "Not available" };
  }
};

export const getCampaignsTableColumns = () => {
  return [
    {
      id: SELECT_COLUMN_ID,
      size: 60,
      enableSorting: false,
      meta: {
        isDisplay: true,
      },
      header: ({ table }) => (
        <Checkbox
          {...{
            checked: table.getIsAllRowsSelected(),
            indeterminate: table.getIsSomeRowsSelected(),
            onCheckedChange: checked => table.toggleAllRowsSelected(!!checked),
            "aria-label": "Select all rows",
          }}
        />
      ),
      cell: ({ row }) => (
        <div className="px-1">
          <Checkbox
            {...{
              checked: row.getIsSelected(),
              disabled: !row.getCanSelect(),
              indeterminate: row.getIsSomeSelected() && !row.getIsSelected(),
              onCheckedChange: checked => row.toggleSelected(!!checked),
              "aria-label": "Select row",
            }}
          />
        </div>
      ),
    },
    {
      header: "ID",
      id: "ID",
      accessorKey: "id",
      size: 60,
      cell: ({ row }) => (
        <TableLink href={`/campaigns/${row.getValue("ID")}`}>
          {row.getValue("ID")}
        </TableLink>
      ),
    },
    {
      header: "Name & Description",
      id: "Name & Description",
      accessorKey: "name",
      enableSorting: false,
      cell: ({ row }) => (
        <div>
          <TableLink href={"/campaigns/" + row.getValue("ID")}>
            {row.getValue("Name & Description")}
          </TableLink>
          <div>{row.original.info.description}</div>
        </div>
      ),
    },

    {
      header: "Start Date",
      id: "Start Date",
      accessorKey: "start_time",
      enableSorting: false,
      cell: ({ row }) => (
        <div>
          {row.getValue("Start Date")
            ? moment(row.getValue("Start Date")).format("YYYY-MM-DD")
            : null}
        </div>
      ),
    },
    {
      header: "End Date",
      id: "End Date",
      accessorKey: "end_time",
      enableSorting: false,
      cell: ({ row }) => (
        <div>
          {row.getValue("End Date")
            ? moment(row.getValue("End Date")).format("YYYY-MM-DD")
            : null}
        </div>
      ),
    },

    {
      header: "# Entities",
      id: "# Entities",
      accessorKey: "metrics.entity_count",
    },
    {
      header: "# Contacts",
      id: "# Contacts",
      accessorKey: "metrics.contact_count",
    },
    {
      header: "# Callable Contacts",
      id: "# Callable Contacts",
      accessorKey: "metrics.callable_contact_count",
    },
    {
      header: "# Emailable Contacts",
      id: "# Emailable Contacts",
      accessorKey: "metrics.emailable_contact_count",
    },
    {
      header: "# Entities Notified",
      id: "# Entities Notified",
      accessorKey: "metrics.unique_entity",
    },

    {
      header: "Open Rate",
      id: "Open Rate",
      accessorKey: "metrics.unique_opened",
      cell: ({ row }) => {
        const value = row.getValue("Open Rate");
        const metrics = row.original.metrics;

        if (value !== undefined) {
          return <div>{getPercentage(value, metrics.delivered)}</div>;
        }
      },
    },

    {
      header: "Bounce Rate",
      id: "Bounce Rate",
      accessorKey: "metrics.bounced",
      cell: ({ row }) => {
        const value = row.getValue("Bounce Rate");
        const metrics = row.original.metrics;

        if (value !== undefined) {
          return <div>{getPercentage(value, metrics.delivered)}</div>;
        }
      },
    },
    {
      header: "Clicked Rate",
      id: "Clicked Rate",
      accessorKey: "metrics.unique_clicked",
      cell: ({ row }) => {
        const value = row.getValue("Clicked Rate");
        const metrics = row.original.metrics;
        if (value !== undefined) {
          return <div>{getPercentage(value, metrics.unique_opened)}</div>;
        }
      },
    },
    {
      header: "Response Rate",
      id: "Response Rate",
      accessorKey: "metrics.unique_responded",
      cell: ({ row }) => {
        const value = row.getValue("Response Rate");
        const metrics = row.original.metrics;
        if (value !== undefined) {
          return <div>{getPercentage(value, metrics.delivered)}</div>;
        }
      },
    },
    {
      header: "Assignee",
      id: "Assignee",
      accessorKey: "assigned_user_full_name",
    },
    {
      header: "Status",
      id: "Status",
      accessorKey: "status",
      cell: ({ row }) => {
        const statusId = row.getValue("Status");
        const { color, content } = getCampaignStatusDisplay(statusId);
        return <Label size="tiny" color={color} content={content} />;
      },
    },
    {
      header: "Region",
      id: "Region",
      accessorKey: "region_name",
    },

    {
      header: <div></div>,
      id: "Actions",
      accessorKey: "actions",
      size: 60,
      enableSorting: false,
      meta: {
        isDisplay: true,
      },
      cell: ({ row }) => (
        <CampaignsTableActions
          campaignId={row.getValue("ID")}
          campaignStatus={row.getValue("Status")}
        />
      ),
    },
  ];
};

export const getCampaignsTableFilters = ({ regions }) => {
  return [
    {
      key: "start_date",
      title: "Start Date",
      type: "dateRange",
    },
    {
      key: "end_date",
      title: "End Date",
      type: "dateRange",
    },
    {
      key: "regions",
      title: "Region",
      type: "select",
      data: regions,
    },
    {
      key: "assignees",
      title: "Assignee",
      type: "ruvixxSelect",
      queryFn: getUsersForFiltersQuery,
      props: {
        includeNoneOption: true,
      },
    },
    {
      key: "status",
      title: "Status",
      type: "select",
      data: campaignConstants.status,
    },
    {
      key: "entity_count",
      title: "# Entities",
      type: "range",
    },
    {
      key: "contact_count",
      title: "# Contacts",
      type: "range",
    },
    {
      key: "callable_contact_count",
      title: "# Callable Contacts",
      type: "range",
    },
    {
      key: "unique_entity",
      title: "# Entities Notified",
      type: "range",
    },
    {
      key: "unique_opened",
      title: "Open Rate",
      type: "range",
    },
    {
      key: "bounced",
      title: "Bounce Rate",
      type: "range",
    },
    {
      key: "unique_clicked",
      title: "Clicked Rate",
      type: "range",
    },
    {
      key: "unique_responded",
      title: "Response Rate",
      type: "range",
    },
  ];
};
